import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { Figure, useCodeLabel, useFormats } from '@/pages/bacen/shared';
import { fetchCrsfnAcordao } from '@/services/bacen';

/**
 * One acordao: the head from the index, the bench with each member's
 * position (a roll call plus the named losers; WITH_MAJORITY is a
 * subtraction and the column says so), the per-party dispositions with the
 * span each was read from, the ledger rows it decided, and the RELATORIO /
 * VOTO / dispositive text when the site served it.
 */
export function CrsfnAcordaoPage() {
  const { key = '' } = useParams();
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'crsfn', 'acordao', key],
    queryFn: () => fetchCrsfnAcordao(key),
    enabled: key.length > 0,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="p-6">
        <ErrorMessage
          message={notFound ? t('bacen:crsfnAcordao.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const a = data.acordao;
  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link
            to="/bacen/crsfn/search"
            className="text-primary underline-offset-2 hover:underline"
          >
            {t('bacen:crsfnAcordao.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold text-foreground">
          {t('bacen:case.acordaos.acordao', { n: a.numero_acordao })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {a.session_raw ? t('bacen:case.acordaos.session', { s: a.session_raw }) : null}
          {a.judgment_date ? ` · ${a.judgment_date}` : null}
          {a.publication_date
            ? ` · ${t('bacen:crsfnAcordao.published', { d: a.publication_date })}`
            : null}
        </p>
      </header>

      <ExplainerCard i18nKey="bacen:crsfnAcordao.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Figure
          label={t('bacen:crsfnAcordao.head.relator')}
          value={a.relator ?? '—'}
          href={a.relator_slug ? `/bacen/crsfn/r/${a.relator_slug}` : undefined}
          testId="relator"
        />
        <Figure
          label={t('bacen:crsfnAcordao.head.appealTypes')}
          value={a.appeal_types.map((c) => label('appealType', c)).join(', ') || '—'}
        />
        <Figure
          label={t('bacen:crsfnAcordao.head.unanimity')}
          value={
            a.is_unanimous === null
              ? '—'
              : t(a.is_unanimous ? 'bacen:case.acordaos.unanimous' : 'bacen:case.acordaos.majority')
          }
          second={`${label('parseStatus', a.parse_status)} · ${label('series', a.series)}`}
        />
        <Figure
          label={t('bacen:crsfnAcordao.head.origin')}
          value={t(`bacen:codes.originHint.${a.origin_hint}`, {
            defaultValue: a.origin_hint,
          })}
          second={a.origin_process ?? undefined}
        />
      </dl>

      {a.key_conflict ? (
        <p
          className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-xs text-foreground"
          role="note"
        >
          {t('bacen:crsfnAcordao.keyConflict', { ids: a.source_ids.length })}
        </p>
      ) : null}

      {a.ementa ? (
        <section className="rounded-lg border bg-card p-4" data-ementa>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('bacen:crsfnAcordao.ementa')}
          </h2>
          <p className="whitespace-pre-line text-sm text-muted-foreground">{a.ementa}</p>
        </section>
      ) : null}

      {a.dispositive_text ? (
        <section className="rounded-lg border bg-card p-4" data-dispositive>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('bacen:crsfnAcordao.dispositive')}
          </h2>
          <p className="whitespace-pre-line text-sm text-muted-foreground">{a.dispositive_text}</p>
        </section>
      ) : null}

      <section className="rounded-lg border bg-card p-4" data-bench>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnAcordao.bench.title')}
        </h2>
        {data.bench.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('bacen:crsfnAcordao.bench.none')}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('bacen:crsfnAcordao.bench.member')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:crsfnAcordao.bench.role')}</th>
                  <th className="px-3 py-2 font-medium">
                    {t('bacen:crsfnAcordao.bench.position')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('bacen:crsfnAcordao.bench.basis')}</th>
                </tr>
              </thead>
              <tbody>
                {data.bench.map((b, i) => (
                  <tr key={`${b.slug}-${b.role}-${i}`} className="border-t" data-member={b.slug}>
                    <td className="px-3 py-2 text-foreground">
                      {b.name}
                      {b.is_substitute ? (
                        <span className="ml-1 text-xs text-muted-foreground">
                          {t('bacen:crsfnAcordao.bench.substitute')}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {label('benchRole', b.role)}
                    </td>
                    <td className="px-3 py-2 text-foreground">
                      {b.position ? label('position', b.position) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {b.basis ? label('voteBasis', b.basis) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:crsfnAcordao.guide.bench" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-dispositions>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnAcordao.dispositions.title')}
        </h2>
        {data.dispositions.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t('bacen:crsfnAcordao.dispositions.none')}
          </p>
        ) : (
          <ul className="space-y-2 text-sm">
            {data.dispositions.map((d, i) => (
              <li key={i} className="rounded-md border bg-muted/30 p-3" data-item={d.item_label}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-mono text-xs text-muted-foreground">{d.item_label}</span>
                  <span className="text-foreground">{d.party_name ?? '—'}</span>
                  <span className="font-medium text-foreground">
                    {label('disposition', d.disposition_code)}
                  </span>
                  {d.penalty_type ? (
                    <span className="text-muted-foreground">
                      {label('penalty', d.penalty_type)}
                    </span>
                  ) : null}
                  {d.amount !== null ? (
                    <span className="tabular-nums text-muted-foreground">
                      {label('amountRole', d.amount_role)} {currency.format(d.amount)}
                    </span>
                  ) : null}
                  {d.original_amount !== null ? (
                    <span className="tabular-nums text-xs text-muted-foreground">
                      (
                      {t('bacen:crsfnAcordao.dispositions.original', {
                        v: currency.format(d.original_amount),
                      })}
                      )
                    </span>
                  ) : null}
                  <span className="text-xs text-muted-foreground">
                    {d.is_unanimous === null
                      ? null
                      : t(
                          d.is_unanimous
                            ? 'bacen:case.acordaos.unanimous'
                            : 'bacen:case.acordaos.majority',
                        )}
                    {d.vencidos.length > 0
                      ? ` · ${t('bacen:crsfnAcordao.dispositions.vencidos', { names: d.vencidos.join(', ') })}`
                      : null}
                    {' · '}
                    {label('parseStatus', d.parse_status)}
                  </span>
                </div>
                {d.provisions.length > 0 ? (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {d.provisions.join(' · ')}
                  </div>
                ) : null}
                <blockquote className="mt-1 border-l-2 pl-2 text-xs text-muted-foreground">
                  {d.span}
                </blockquote>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg border bg-card p-4" data-ledger-rows>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnAcordao.ledger.title', {
            n: number.format(data.ledger_rows.length),
          })}
        </h2>
        {data.ledger_rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('bacen:crsfnAcordao.ledger.none')}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.pas')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.accused')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.first')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.board')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:crsfnAcordao.ledger.match')}</th>
                </tr>
              </thead>
              <tbody>
                {data.ledger_rows.map((r) => (
                  <tr key={r.row_index} className="border-t" data-row={r.row_index}>
                    <td className="px-3 py-2 tabular-nums">
                      <Link
                        to={`/bacen/c/${r.pas_number}`}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {r.pas_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-foreground">
                      {r.subject_name}
                      <span className="ml-1 text-xs text-muted-foreground">
                        {label('subjectType', r.subject_type)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {label('penalty', r.first_penalty_code)}
                      {r.first_fine !== null ? ` ${currency.format(r.first_fine)}` : null}
                    </td>
                    <td className="px-3 py-2 text-foreground">
                      {label('penalty', r.board_penalty_code)}
                      {r.board_fine !== null ? ` ${currency.format(r.board_fine)}` : null}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {t(
                        r.party_matched
                          ? 'bacen:crsfnAcordao.ledger.partyMatched'
                          : 'bacen:crsfnAcordao.ledger.partyUnmatched',
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-lg border bg-card p-4" data-text>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnAcordao.text.title')}
        </h2>
        {data.text ? (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {t('bacen:crsfnAcordao.text.shape', {
                chars: number.format(data.text.char_count),
                status: label('parseStatus', data.text.section_status),
              })}
              {a.source_link ? (
                <>
                  {' · '}
                  <a
                    href={a.source_link}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {t('bacen:crsfnAcordao.text.source')}
                  </a>
                </>
              ) : null}
            </p>
            {(
              [
                ['relatorio', data.text.relatorio],
                ['voto', data.text.voto],
                ['dispositivo', data.text.dispositivo],
              ] as const
            ).map(([section, body]) =>
              body ? (
                <details key={section} className="rounded-md border" data-section={section}>
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-foreground">
                    {t(`bacen:crsfnAcordao.text.${section}`)}
                  </summary>
                  <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap border-t px-3 py-2 font-sans text-xs text-muted-foreground">
                    {body}
                  </pre>
                </details>
              ) : null,
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{t('bacen:crsfnAcordao.text.none')}</p>
        )}
      </section>
    </div>
  );
}
